import {Box, HStack, Icon, Show, Text, ThemeColorPath, VStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';
import styled from 'styled-components';
import {match, Pattern} from 'ts-pattern';

import {isNotNil} from 'ramda';

import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId, useBoolean} from 'shared/web';

import {RateMatrixCellData} from '../types/RateMatrixCellData';
import {createActivationKeyDownHandler} from '../utils/createActivationKeyDownHandler';

const CELL_MIN_HEIGHT = 17;
const EDIT_ICON_SIZE = 3;
const EMPTY_CELL_ICON_SIZE = 4;

const CELL_APPEARANCE = {
  emptyDefault: {
    border: '1px dashed',
    borderColor: 'general.separator',
    backgroundColor: 'palettes.neutral.10.100',
  },
  emptyHover: {
    border: '1px dashed',
    borderColor: 'palettes.blue.40.100',
    backgroundColor: 'palettes.blue.10.100',
  },
  configuredDefault: {
    border: '1px solid',
    borderColor: 'palettes.blue.40.100',
    backgroundColor: 'palettes.blue.10.100',
  },
  configuredHover: {
    border: '1px solid',
    borderColor: 'palettes.blue.80.100',
    backgroundColor: 'palettes.blue.30.100',
  },
} as const;

const SELECTED_BORDER_COLOR = 'palettes.blue.60.100';

interface CellSurfaceProps {
  $isDisabled: boolean;
}

/**
 * The cell's own interactive surface — the shared `Clickable` has no disabled a11y contract
 * (`aria-disabled`, forced `tabindex="-1"`) and no design-system focus ring, so the cell carries
 * both itself, mirroring the vehicle financing matrix cell.
 */
const CellSurface = styled.div<CellSurfaceProps>`
  width: 100%;
  height: 100%;
  /* Shift-activating a cell toggles the bulk-edit selection; without this, shift-click also
     drag-selects the cell text. */
  user-select: none;
  cursor: ${({$isDisabled}) => ($isDisabled ? 'initial' : 'pointer')};
  pointer-events: ${({$isDisabled}) => ($isDisabled ? 'none' : 'all')};

  /* The design-system focus ring: theme.shadows.active is the semantic focus/active token every
     platform input shares, so a keyboard-focused cell reads the same as a focused input instead of
     falling back to the browser's default outline. The radius matches the inner cell box. */
  &:focus-visible {
    outline: none;
    border-radius: ${({theme}) => theme.radii.small};
    box-shadow: ${({theme}) => theme.shadows.active};
  }
`;

interface RateMatrixCellProps extends RequiredTestIdProps {
  cellData: RateMatrixCellData | undefined;
  ariaLabel: string;
  isReadOnly: boolean;
  isDisabled?: boolean;
  isSelected?: boolean;
  /**
   * `isMultiSelect` is true when the cell was activated with Shift held down. Bulk-edit-aware
   * callers add the cell to their selection instead of opening the single-cell dialog.
   */
  onActivate: (isMultiSelect: boolean) => void;
}

export function RateMatrixCell(props: RateMatrixCellProps) {
  const formatNumber = useFormatNumber();
  const [isHovered, handleMouseEnter, handleMouseLeave] = useBoolean(false);
  const [isFocused, handleFocus, handleBlur] = useBoolean(false);

  const isInteractionBlocked = props.isReadOnly || props.isDisabled === true;
  const cellData = props.cellData;
  const isConfigured = isNotNil(cellData);
  const isHighlighted = isHovered || isFocused;

  const appearance = match({isConfigured, isHighlighted})
    .with({isConfigured: false, isHighlighted: false}, () => CELL_APPEARANCE.emptyDefault)
    .with({isConfigured: false, isHighlighted: true}, () => CELL_APPEARANCE.emptyHover)
    .with({isConfigured: true, isHighlighted: false}, () => CELL_APPEARANCE.configuredDefault)
    .with({isConfigured: true, isHighlighted: true}, () => CELL_APPEARANCE.configuredHover)
    .exhaustive();

  const emptyCellIconColor = match<{isSelected: boolean; isHighlighted: boolean}, ThemeColorPath>({
    isSelected: props.isSelected === true,
    isHighlighted,
  })
    .with({isSelected: true}, () => 'general.accent')
    .with({isSelected: false, isHighlighted: true}, () => 'palettes.blue.40.100')
    .with({isSelected: false, isHighlighted: false}, () => 'text.tertiary')
    .exhaustive();

  return (
    <CellSurface
      role="button"
      tabIndex={isInteractionBlocked ? -1 : 0}
      aria-label={props.ariaLabel}
      aria-disabled={isInteractionBlocked || undefined}
      aria-pressed={props.isSelected}
      $isDisabled={isInteractionBlocked}
      onClick={isInteractionBlocked ? undefined : (event) => props.onActivate(event.shiftKey)}
      onKeyDown={
        isInteractionBlocked
          ? undefined
          : createActivationKeyDownHandler((event) => props.onActivate(event.shiftKey))
      }
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      data-testid={suffixTestId('rateMatrixCell', props)}
    >
      <Box
        padding={2}
        height="100%"
        minHeight={CELL_MIN_HEIGHT}
        border={appearance.border}
        borderRadius="small"
        borderColor={props.isSelected === true ? SELECTED_BORDER_COLOR : appearance.borderColor}
        backgroundColor={appearance.backgroundColor}
      >
        {match(cellData)
          .with(Pattern.nonNullable, (data) => (
            <HStack align="flex-start" justify="space-between" height="100%" width="100%">
              <Box width={EDIT_ICON_SIZE} height={EDIT_ICON_SIZE}>
                <Show when={isHighlighted}>
                  <span aria-hidden="true">
                    <Icon value="image/edit" size={EDIT_ICON_SIZE} color="palettes.blue.80.100" />
                  </span>
                </Show>
              </Box>
              <VStack align="center" justify="center" grow={1} height="100%">
                <Text size="base" alternative color="link">
                  {`${formatNumber(Number(data.nominalRate))} %`}
                </Text>
                <Text size="xxSmall" alternative color={isHighlighted ? 'link' : 'tertiary'}>
                  {`${formatNumber(Number(data.apr))} % ${i18n.t('entity.vehicleFinancingTemplate.labels.apr')}`}
                </Text>
              </VStack>
              <Box width={EDIT_ICON_SIZE} height={EDIT_ICON_SIZE}>
                <Show when={props.isSelected}>
                  <span aria-hidden="true">
                    <Icon
                      value="action/check_circle"
                      size={EDIT_ICON_SIZE}
                      color="general.accent"
                    />
                  </span>
                </Show>
              </Box>
            </HStack>
          ))
          .otherwise(() => (
            <VStack align="center" justify="center" height="100%">
              <span aria-hidden="true">
                <Icon
                  value={props.isSelected === true ? 'action/check_circle' : 'content/add'}
                  size={EMPTY_CELL_ICON_SIZE}
                  color={emptyCellIconColor}
                />
              </span>
            </VStack>
          ))}
      </Box>
    </CellSurface>
  );
}
